import { computed, Injectable, signal } from '@angular/core';
import assertNever from 'assert-never';
import { Subject } from 'rxjs';

import { SystemState, SystemStatus } from '@shure/cloud/shared/models/devices';

export enum AppUpdateState {
	/**
	 * The app is in a normal, operational state
	 */
	Operational = 'operational',
	/**
	 * The app is currently updating and is in a read-only mode.
	 */
	InProgress = 'in-progress',
	/**
	 * The update is complete and it is safe to exit read-only mode and continue using the app.
	 */
	Complete = 'complete',
	/**
	 * The update is complete, but a full reload is required due to a backend schema change.
	 */
	CompleteReloadRequired = 'complete-reload-required'
}

/**
 * Service that handles the state transitions that occur during an app update. It will progress through the states by going
 * from Operational -> InProgress -> Complete/CompleteReloadRequired.
 */
@Injectable({ providedIn: 'root' })
export class AppUpdateService {
	private isReloadRequired = false;

	public readonly state = signal(AppUpdateState.Operational);

	public readonly isReadOnly = computed(() => {
		const state = this.state();
		return state === AppUpdateState.InProgress || state === AppUpdateState.CompleteReloadRequired;
	});

	/**
	 * Subject that emits a single event once the app update is complete.
	 * The boolean parameter indicates if an app reload is required.
	 */
	public readonly onUpdateComplete = new Subject<boolean>();

	public handleSystemStatusChange(newSystemStatus: SystemStatus): void {
		switch (newSystemStatus.state) {
			case SystemState.Limited:
			case SystemState.Suspended:
				this.handleSystemStatusLimitedOrSuspended(newSystemStatus);
				break;
			case SystemState.Operational:
				this.handleSystemStatusOperational();
				break;
			default:
				assertNever(newSystemStatus.state);
		}
	}

	/**
	 * Handles the transition from operational to update in progress. We also capture the schema changed status
	 * at this point to determine if an app reload is required before returning to write-safe mode.
	 */
	private handleSystemStatusLimitedOrSuspended(systemStatus: SystemStatus): void {
		if (this.state() === AppUpdateState.Operational) {
			this.state.set(AppUpdateState.InProgress);
			this.isReloadRequired = systemStatus.schemaChanged;
		}
	}

	/**
	 * Handles the transition from update in progress back to operational.
	 */
	private handleSystemStatusOperational(): void {
		if (this.state() === AppUpdateState.InProgress) {
			this.state.set(this.isReloadRequired ? AppUpdateState.CompleteReloadRequired : AppUpdateState.Complete);
			this.onUpdateComplete.next(this.isReloadRequired);
			this.onUpdateComplete.complete();
		}
	}
}
